import { useState, useRef, useEffect } from 'react';
import { IconButton } from '@blacknbrownstudios/ui';
import type { Conversation } from '../types';

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onRename: (title: string) => void;
}

export function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onDelete,
  onRename,
}: ConversationItemProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conversation.title);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    // Clamp at 0: if the server's timestamp is slightly ahead of the client
    // clock, Math.floor on a negative number returns -1 and the UI shows
    // "-1 days ago" for a fresh conversation. Treat any future-dated row as
    // "Today" — the diff is always small in practice.
    const days = Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditTitle(conversation.title);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const trimmedTitle = editTitle.trim();
    if (trimmedTitle && trimmedTitle !== conversation.title) {
      onRename(trimmedTitle);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditTitle(conversation.title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <div
      className={`group relative rounded-lg transition-colors cursor-pointer ${
        isActive ? 'bg-primary-900/30 text-white' : 'hover:bg-neutral-800/50 text-neutral-300'
      }`}
      onClick={isEditing ? undefined : onSelect}
      onDoubleClick={handleStartEdit}
      onMouseEnter={() => setShowMenu(true)}
      onMouseLeave={() => setShowMenu(false)}
    >
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 bg-neutral-800 border border-primary-500 rounded px-2 py-0.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          ) : (
            <span className="font-medium text-sm truncate flex-1">{conversation.title}</span>
          )}

          {/* Menu buttons */}
          {showMenu && !isEditing && (
            <div className="flex items-center gap-1">
              {/* Edit button */}
              <IconButton
                onClick={handleStartEdit}
                size="small"
                className="opacity-60 hover:opacity-100 hover:bg-neutral-700"
                aria-label="Rename"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </IconButton>

              {/* Delete button */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm('Delete this conversation?')) {
                    onDelete();
                  }
                }}
                size="small"
                className="opacity-60 hover:opacity-100 hover:bg-neutral-700"
                aria-label="Delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </IconButton>
            </div>
          )}
        </div>

        <div className="text-xs text-neutral-500 mt-0.5">{formatDate(conversation.updated_at)}</div>
      </div>
    </div>
  );
}
